import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { type ReactChild, forwardRef, useImperativeHandle, useRef, useState } from "react";
import styles from "./style.module.scss";

type ManageActionPanelProps = {
  onCopy?: () => void;
  onReplace?: () => void;
  onRemove?: () => void;
  onHide?: () => void;
  isVisible?: boolean;
  pastDisabled?: boolean;
  disabled?: boolean;
};

type ActionButtonProps = React.HTMLAttributes<HTMLButtonElement> & {
  onAction?: () => void;
  disabled?: boolean;
  children: ReactChild;
  defaultTooltipText: string;
  toggledTooltipText?: string;
};

type ActionButtonHandle = {
  showToggledTooltip: () => void;
};

// extract to molecules or atoms?
const ActionButton = forwardRef<ActionButtonHandle, ActionButtonProps>(
  ({ defaultTooltipText, toggledTooltipText, onAction, disabled, children, ...restProps }, ref) => {
    const [toggled, setToggled] = useState(false);

    useImperativeHandle(ref, () => ({
      showToggledTooltip: () => {
        setToggled(true);
      },
    }));

    return (
      <mm-tooltip-wrapper
        disabled={disabled}
        tail={true}
        position="bottom"
        tooltip-text={toggled ? toggledTooltipText : defaultTooltipText}
      >
        <button
          className={styles["action-button"]}
          onClick={(event) => {
            event.stopPropagation();
            onAction?.();
          }}
          disabled={disabled}
          type="button"
          {...restProps}
        >
          {children}
        </button>
      </mm-tooltip-wrapper>
    );
  },
);

function ManageActionPanel({
  onCopy,
  onReplace,
  onRemove,
  onHide,
  isVisible = true,
  pastDisabled,
  disabled,
}: ManageActionPanelProps) {
  const replaceButtonRef = useRef<ActionButtonHandle>(null);
  const copyButtonRef = useRef<ActionButtonHandle>(null);

  function handleCopy() {
    copyButtonRef.current?.showToggledTooltip();
    onCopy?.();
  }

  function handleLayerReplace() {
    replaceButtonRef.current?.showToggledTooltip();
    onReplace?.();
  }

  return (
    <div className={styles["action-buttons-row"]}>
      {!!onCopy && (
        <ActionButton
          ref={copyButtonRef}
          defaultTooltipText={t`Copy`}
          toggledTooltipText={t`Copied!`}
          onAction={() => handleCopy()}
          disabled={disabled}
          data-testid={"layerstack-copy-btn"}
        >
          <span className={classNames("material-icons", styles["action-button__icon"])}>layers</span>
        </ActionButton>
      )}
      {!!onReplace && (
        <ActionButton
          ref={replaceButtonRef}
          defaultTooltipText={t`Replace`}
          toggledTooltipText={t`Replaced!`}
          onAction={() => handleLayerReplace()}
          disabled={pastDisabled}
          data-testid={"layerstack-replace-btn"}
        >
          <span className={classNames("material-icons", styles["action-button__icon"])}>content_paste</span>
        </ActionButton>
      )}
      {!!onHide && (
        <ActionButton
          defaultTooltipText={!isVisible ? t`Show` : t`Hide`}
          onAction={onHide}
          data-testid={"layerstack-visibility-btn"}
        >
          <span className={classNames("material-icons", styles["action-button__icon"])}>
            {isVisible ? "visibility" : "visibility_off"}
          </span>
        </ActionButton>
      )}
      {!!onRemove && (
        <ActionButton
          ref={replaceButtonRef}
          defaultTooltipText={t`Delete`}
          onAction={onRemove}
          data-testid={"layerstack-delete-btn"}
        >
          <span className={classNames("material-icons", styles["action-button__icon"])}>delete</span>
        </ActionButton>
      )}
    </div>
  );
}

export { ManageActionPanel };
